import React from "react";
import {
  BoxPanel,
  Grid,
  Statistics,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@tiny/components";
import ComponentLoader from "@tiny/components/src/ComponentLoader/ComponentLoader";
import { formatNumber } from "@tiny/components/src/utils/formatUtils";
import EngagerChart from "./EngagerChart";
import { useSegmentPerformanceDialog } from "./SegmentPerformanceDialog.hooks";
import {
  useStyles,
  getHighlightClass,
  getIspHighlightClass,
} from "./SegmentPerformanceDialog.styles";
import { COLUMNS, formatDay } from "./SegmentPerformanceDialog.helpers";
import { IspEngagementItemView } from "@tiny/api";
import ChevronRight from "@tiny/icons/generated/ChevronRight";

const ISP_COLUMNS: {
  key: keyof IspEngagementItemView;
  isPercent?: boolean;
  highlight?: string;
}[] = [
  { key: "send" },
  { key: "delivered" },
  { key: "opens", highlight: "opens-start" },
  { key: "openRateOnDelivered", isPercent: true, highlight: "opens-end" },
  { key: "clicks", highlight: "clicks-start" },
  { key: "clickRateOnDelivered", isPercent: true, highlight: "clicks-end" },
  { key: "spam", highlight: "spam-start" },
  { key: "spamRateOnDelivered", isPercent: true, highlight: "spam-end" },
  { key: "unsubscribe" },
  { key: "unsubscribeRateOnClick", isPercent: true },
];

export interface SegmentPerformanceContentProps {
  open: boolean;
  segmentId: string;
  days: number;
}

function SegmentPerformanceContent({
  open,
  segmentId,
  days,
}: SegmentPerformanceContentProps) {
  const theme = useTheme();
  const classes = useStyles();
  const {
    rows,
    isLoaded,
    isLoading,
    hoveredIndex,
    handleHoverIndex,
    totals,
    chartData,
    selectedDate,
    ispData,
    ispLoading,
    handleDateClick,
  } = useSegmentPerformanceDialog({ open, segmentId, days });

  return (
    <>
      <BoxPanel
        title={`Open Rate — Last ${days} Days`}
        variant="footerBackground"
        footer={
          isLoaded ? (
            <Grid container verticalSpacing={4}>
              <Grid item xs={3} flex="1">
                <Statistics
                  value={formatNumber(totals.send)}
                  title="Emails Sent"
                  valueVariant="h3"
                  bullet
                  bulletColor={theme.palette.grey[700]}
                />
              </Grid>
              <Grid item xs={3} flex="1">
                <Statistics
                  value={formatNumber(totals.delivered)}
                  title="Delivered"
                  valueVariant="h3"
                  bullet
                  bulletColor={theme.palette.grey[400]}
                />
              </Grid>
              <Grid item xs={3} flex="1">
                <Statistics
                  value={formatNumber(totals.opens)}
                  title="Total Opens"
                  valueVariant="h3"
                  bullet
                  bulletColor={theme.palette.secondary.main}
                />
              </Grid>
              <Grid item xs={3} flex="1">
                <Statistics
                  value={formatNumber(totals.clicks)}
                  title="Total Clicks"
                  valueVariant="h3"
                  bullet
                  bulletColor={theme.palette.primary.main}
                />
              </Grid>
            </Grid>
          ) : null
        }
      >
        {isLoading ? (
          <ComponentLoader height="220px" />
        ) : chartData.length > 0 ? (
          <EngagerChart
            data={chartData}
            height={220}
            hoveredIndex={hoveredIndex}
            onHoverIndex={handleHoverIndex}
          />
        ) : isLoaded ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: 220,
            }}
          >
            <Typography color="grey" tone={600} variant="body2">
              No data for the last {days} days
            </Typography>
          </div>
        ) : null}
      </BoxPanel>

      <div className={classes.section}>
        <BoxPanel title="Daily Breakdown">
          {isLoading ? (
            <ComponentLoader height="200px" />
          ) : rows.length > 0 ? (
            <Table hideLastRowBorder headerBg="#F3F4F6">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography variant="label2" color="grey" tone={900}>
                      Date
                    </Typography>
                  </TableCell>
                  {COLUMNS.map(({ label }) => (
                    <TableCell key={label}>
                      <Typography variant="label2" color="grey" tone={900}>
                        {label}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, i) => {
                  const isExpanded =
                    row.date != null && selectedDate === row.date;
                  return (
                    <React.Fragment key={i}>
                      <TableRow
                        className={
                          isExpanded
                            ? classes.expandedParentRow
                            : hoveredIndex === i
                            ? classes.highlightedRow
                            : classes.normalRow
                        }
                        style={{
                          cursor: row.date != null ? "pointer" : undefined,
                        }}
                        onClick={() =>
                          row.date != null && handleDateClick(row.date)
                        }
                      >
                        <TableCell>
                          <div className={classes.dateCellContent}>
                            <span
                              className={`${classes.expandIcon}${
                                isExpanded ? ` ${classes.expandIconOpen}` : ""
                              }`}
                            >
                              <ChevronRight style={{ width: 18, height: 18 }} />
                            </span>
                            {isExpanded || hoveredIndex === i ? (
                              <Typography
                                variant="label1"
                                color="primary"
                                tone="main"
                              >
                                {row.date != null ? formatDay(row.date) : "—"}
                              </Typography>
                            ) : (
                              <Typography
                                variant="label1"
                                color="grey"
                                tone={900}
                              >
                                {row.date != null ? formatDay(row.date) : "—"}
                              </Typography>
                            )}
                          </div>
                        </TableCell>
                        {COLUMNS.map(({ label, key, isPercent, highlight }) => {
                          const val = row[key] ?? 0;
                          const display = isPercent
                            ? `${(val as number).toFixed(1)}%`
                            : formatNumber(val as number);
                          return (
                            <TableCell
                              key={label}
                              className={getHighlightClass(highlight, classes)}
                            >
                              <Typography
                                variant="body2"
                                color="grey"
                                tone={900}
                                fontWeight={
                                  hoveredIndex === i ? "bold" : "regular"
                                }
                              >
                                {display}
                              </Typography>
                            </TableCell>
                          );
                        })}
                      </TableRow>

                      {/* ISP breakdown rows — inline for perfect column alignment */}
                      {isExpanded && (
                        <>
                          {ispLoading ? (
                            <TableRow className={classes.ispRow}>
                              <TableCell
                                colSpan={COLUMNS.length + 1}
                                style={{ padding: 0 }}
                              >
                                <ComponentLoader height="60px" />
                              </TableCell>
                            </TableRow>
                          ) : ispData.length === 0 ? (
                            <TableRow className={classes.ispRow}>
                              <TableCell
                                colSpan={COLUMNS.length + 1}
                                style={{ padding: "12px 20px" }}
                              >
                                <Typography
                                  color="grey"
                                  tone={500}
                                  variant="body2"
                                >
                                  No ISP data available
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            ispData.map((isp, j) => (
                              <TableRow
                                key={j}
                                className={`${classes.ispRow}${
                                  j === ispData.length - 1
                                    ? ` ${classes.ispRowLast}`
                                    : ""
                                }`}
                              >
                                <TableCell style={{ paddingLeft: 40 }}>
                                  <Typography
                                    variant="label1"
                                    color="grey"
                                    tone={900}
                                  >
                                    {isp.isp ?? "—"}
                                  </Typography>
                                </TableCell>
                                {ISP_COLUMNS.map(
                                  ({ key, isPercent, highlight }, k) => {
                                    const val = isp[key] ?? 0;
                                    const display = isPercent
                                      ? `${(val as number).toFixed(1)}%`
                                      : formatNumber(val as number);
                                    return (
                                      <TableCell
                                        key={k}
                                        className={getIspHighlightClass(
                                          highlight,
                                          classes
                                        )}
                                      >
                                        <Typography
                                          variant="body2"
                                          color="grey"
                                          tone={900}
                                        >
                                          {display}
                                        </Typography>
                                      </TableCell>
                                    );
                                  }
                                )}
                              </TableRow>
                            ))
                          )}
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          ) : isLoaded ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 200,
              }}
            >
              <Typography color="grey" tone={600} variant="body2">
                No data available
              </Typography>
            </div>
          ) : null}
        </BoxPanel>
      </div>
    </>
  );
}

export default SegmentPerformanceContent;
